import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import logo from "./logo.png";
import backgroundVideo from "./glowing_bulb.mp4";

function Hero() {
  const [currentWord, setCurrentWord] = useState("Smart Energy");
  const [isFlipping, setIsFlipping] = useState(false);
  const [isDark, setIsDark] = useState(false);

  const videoRef = useRef(null);
  const wordRef = useRef("Smart Energy");
  const navigate = useNavigate();

  const words = [
    "Smart Energy",
    "Smart Grid",
    "IoT Monitoring",
    "Power Analytics",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setIsFlipping(true);

      setTimeout(() => {
        const idx = words.indexOf(wordRef.current);
        const next = words[(idx + 1) % words.length];

        setCurrentWord(next);
        wordRef.current = next;
      }, 280);

      setTimeout(() => setIsFlipping(false), 600);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const handleTimeUpdate = () => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;

      if (time >= 2.9 && time <= 3.9) {
        setIsDark(true);
      } else {
        setIsDark(false);
      }
    }
  };

  return (
    <section
      className={`relative min-h-screen w-full flex items-center justify-start overflow-hidden transition-colors duration-700 ease-in-out
      ${isDark ? "bg-[#02040a]" : "bg-[#030712]"} text-white`}
    >

      {/* BACKGROUND VIDEO */}
      <video
        ref={videoRef}
        autoPlay
        loop
        muted
        playsInline
        onTimeUpdate={handleTimeUpdate}
        className={`absolute inset-0 w-full h-full object-cover z-0 transition-all duration-700 ease-in-out
        ${
          isDark
            ? "opacity-30 scale-100 filter brightness-50"
            : "opacity-80 scale-100 filter brightness-95 contrast-110"
        }`}
      >
        <source src={backgroundVideo} type="video/mp4" />
      </video>

      {/* AMBIENT GLOW OVERLAY */}
      <div
        className={`absolute inset-0 z-1 pointer-events-none transition-all duration-1000 ease-in-out
        ${
          isDark
            ? "bg-black/50 backdrop-blur-[2px]"
            : "bg-radial-gradient bg-gradient-to-tr from-orange-600/10 via-amber-500/5 to-transparent opacity-100 mix-blend-screen"
        }`}
      />

      {/* DYNAMIC VIGNETTE */}
      <div
        className={`absolute inset-0 z-2 transition-all duration-700 ease-in-out bg-gradient-to-r from-[#030712] via-[#030712]/70 to-transparent
        ${isDark ? "opacity-90" : "opacity-60"}`}
      />

      {/* FLOATING PARTICLES */}
      <div className="absolute inset-0 z-[3] overflow-hidden pointer-events-none">

        <div className="absolute top-[20%] left-[10%] w-2 h-2 bg-orange-400/40 rounded-full animate-floatSlow" />

        <div className="absolute top-[35%] right-[20%] w-3 h-3 bg-yellow-300/30 rounded-full animate-floatFast" />

        <div className="absolute bottom-[25%] left-[30%] w-2 h-2 bg-amber-200/40 rounded-full animate-floatMedium" />

        <div className="absolute bottom-[15%] right-[15%] w-4 h-4 bg-orange-500/20 rounded-full animate-floatSlow" />

      </div>

      {/* MAIN CONTENT */}
      <div className="relative z-10 w-full text-left px-6 sm:px-12 md:pl-24 lg:pl-36 max-w-6xl select-none animate-contentFade">

        {/* PREMIUM LOGO GROUP */}
        <div
          className={`flex justify-start items-center gap-4 mb-10 transition-all duration-700 transform
          ${isDark ? "opacity-50 translate-y-1" : "opacity-100 translate-y-0"}`}
        >
          <div className="relative group">

            <div
              className={`absolute -inset-1 rounded-2xl bg-gradient-to-r from-orange-500 to-yellow-400 opacity-30 blur transition duration-500
              ${isDark ? "opacity-0" : "group-hover:opacity-100"}`}
            />

            <img
              src={logo}
              alt="Power Sense"
              className="relative w-16 h-16 rounded-xl bg-white/95 p-1.5 shadow-2xl transition-transform duration-500 hover:scale-105"
            />
          </div>

          <div className="text-left space-y-0.5">
            <p
              className={`text-xs uppercase tracking-[0.35em] font-black transition-colors duration-700
              ${isDark ? "text-slate-400" : "text-amber-400"}`}
            >
              Power Sense Pvt. Ltd.
            </p>

            <p className="text-xs sm:text-sm text-slate-400 font-medium tracking-wide">
              IoT <span className="text-slate-600">•</span> Smart Grid{" "}
              <span className="text-slate-600">•</span> Energy Management
            </p>
          </div>
        </div>

        {/* PREMIUM HEADING GLOW */}
        <div className="absolute left-0 top-[18%] w-[550px] h-[220px] bg-orange-500/10 blur-[140px] rounded-full pointer-events-none animate-pulse" />

        {/* PREMIUM HEADLINE */}
        <h1 className="relative text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.08] tracking-[-0.03em]">

          {/* FIRST LINE */}
          <span
            className={`block transition-all duration-1000
            ${
              isDark
                ? "text-slate-700"
                : "text-white drop-shadow-[0_4px_25px_rgba(255,255,255,0.08)]"
            }`}
          >
            Make your
          </span>

          {/* ANIMATED WORD */}
          <span
            className={`relative inline-flex items-center mt-2 transition-all duration-500 transform font-black
            bg-gradient-to-r from-orange-400 via-amber-300 to-yellow-200 bg-clip-text text-transparent
            ${
              isFlipping
                ? "opacity-0 -translate-y-5 scale-95 blur-md"
                : "opacity-100 translate-y-0 scale-100 blur-0"
            }
            ${
              !isDark
                ? "drop-shadow-[0_0_40px_rgba(245,158,11,0.4)]"
                : "drop-shadow-none"
            }`}
          >
            {currentWord}

            {/* PREMIUM BLINK CURSOR */}
            <span className="ml-2 text-orange-300 animate-cursorBlink">
              |
            </span>

            {/* GLOW LINE */}
            {!isDark && (
              <span className="absolute -bottom-2 left-0 w-full h-[2px] bg-gradient-to-r from-orange-500/0 via-orange-400 to-orange-500/0 animate-lineGlow" />
            )}
          </span>

          {/* SECOND LINE */}
          <span
            className={`block mt-2 transition-all duration-1000
            ${
              isDark
                ? "text-slate-600"
                : "bg-gradient-to-r from-white via-slate-100 to-slate-400 bg-clip-text text-transparent"
            }`}
          >
            reliable & visible.
          </span>

        </h1>

        {/* DESCRIPTION */}
        <p
          className={`mt-7 text-sm sm:text-lg transition-all duration-700 max-w-2xl font-normal leading-relaxed tracking-wide
          ${isDark ? "text-slate-600" : "text-slate-300"}`}
        >
          Power Sense empowers industrial ecosystems to monitor, analyze, and
          control critical electrical paths in real time. We replace operational
          uncertainty with absolute clarity.
        </p>

        {/* BUTTONS */}
        <div className="mt-12 flex flex-col sm:flex-row justify-start gap-5 items-stretch sm:items-center">

          {/* ENQUIRE BUTTON */}
          <button
            onClick={() => navigate("/enquiry")}
            className={`group relative overflow-hidden px-8 py-4 rounded-xl font-bold uppercase tracking-wider text-sm transition-all duration-500 transform active:scale-95
            ${
              isDark
                ? "bg-slate-800 text-slate-400 border border-slate-700 cursor-not-allowed opacity-60"
                : "bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400 text-neutral-950 hover:scale-105 hover:shadow-[0_0_50px_rgba(245,158,11,0.4)]"
            }`}
            disabled={isDark}
          >

            <span className="relative z-10 flex items-center justify-center gap-2">
              Enquire Now

              <svg
                className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </span>

            {/* LIGHT SWEEP */}
            <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-white/20 to-transparent skew-x-12"></span>

          </button>


        </div>

      </div>

      {/* CORNER GLOW */}
      <div
        className={`absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-orange-500/5 via-transparent to-transparent pointer-events-none transition-opacity duration-1000
        ${isDark ? "opacity-0" : "opacity-100"}`}
      />

      {/* BOTTOM FADE */}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-[#02040a] to-transparent z-[5] pointer-events-none" />

      {/* CUSTOM ANIMATIONS */}
      <style>{`

        @keyframes floatSlow {
          0% { transform: translateY(0px); opacity: 0.4; }
          50% { transform: translateY(-20px); opacity: 1; }
          100% { transform: translateY(0px); opacity: 0.4; }
        }

        @keyframes floatFast {
          0% { transform: translateY(0px); opacity: 0.3; }
          50% { transform: translateY(-35px); opacity: 1; }
          100% { transform: translateY(0px); opacity: 0.3; }
        }

        @keyframes floatMedium {
          0% { transform: translateY(0px); opacity: 0.5; }
          50% { transform: translateY(-25px); opacity: 1; }
          100% { transform: translateY(0px); opacity: 0.5; }
        }

        @keyframes contentFade {
          0% {
            opacity: 0;
            transform: translateY(40px);
          }
          100% {
            opacity: 1;
            transform: translateY(0px);
          }
        }

        @keyframes cursorBlink {
          0%, 50% {
            opacity: 1;
          }
          51%, 100% {
            opacity: 0;
          }
        }

        @keyframes lineGlow {
          0% {
            opacity: 0.3;
            transform: scaleX(0.8);
          }
          50% {
            opacity: 1;
            transform: scaleX(1);
          }
          100% {
            opacity: 0.3;
            transform: scaleX(0.8);
          }
        }

        .animate-floatSlow {
          animation: floatSlow 8s ease-in-out infinite;
        }

        .animate-floatFast {
          animation: floatFast 5s ease-in-out infinite;
        }

        .animate-floatMedium {
          animation: floatMedium 6.5s ease-in-out infinite;
        }

        .animate-contentFade {
          animation: contentFade 1.2s ease-out;
        }

        .animate-cursorBlink {
          animation: cursorBlink 1s infinite;
        }

        .animate-lineGlow {
          animation: lineGlow 3s ease-in-out infinite;
        }

      `}</style>

    </section>
  );
}

export default Hero;